import React, { useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import barcodeService, { PdfDisplayError } from '../services/barcodeService';
import { parseApiError } from '../utils/errorUtils';
import './BarcodePrintModal.css';

interface BarcodePrintModalProps {
  isOpen: boolean;
  productId: number;
  productName: string;
  onClose: () => void;
}

/**
 * Asks how many labels to print and opens the generated A4 PDF in a new tab
 * (the browser's viewer provides the print preview). The default quantity is
 * one full sheet according to the company's barcode sheet design.
 */
const BarcodePrintModal: React.FC<BarcodePrintModalProps> = ({
  isOpen, productId, productName, onClose,
}) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(40);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    // Default to one full sheet from the saved design.
    barcodeService.getSettings()
      .then((res) => {
        const s = res.data.data;
        if (s) setQuantity(Math.max(1, s.rows * s.columns));
      })
      .catch(() => { /* keep the fallback default */ });
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePreview = async () => {
    setError(null);
    setLoading(true);
    // Open the tab synchronously with the click so popup blockers allow it.
    const tab = window.open('', '_blank');
    try {
      await barcodeService.openLabelsPdf(productId, quantity, tab);
    } catch (err: unknown) {
      tab?.close();
      setError(err instanceof PdfDisplayError
        ? t('barcode.popupBlocked')
        : parseApiError(err, t('barcode.failedToGenerate')).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="barcode-print-overlay">
      <div className="barcode-print-modal">
        <div className="barcode-print-header">
          <h2>{t('barcode.printTitle')}</h2>
          <button
            className="barcode-print-close"
            onClick={onClose}
            type="button"
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>

        <div className="barcode-print-body">
          <p className="barcode-print-product">{productName}</p>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show">
              <strong>{t('common.error')}:</strong> {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
                aria-label={t('common.close')}
              ></button>
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="barcode-quantity">
              {t('barcode.quantity')} <span className="required">*</span>
            </label>
            <input
              id="barcode-quantity"
              type="number"
              className="form-input"
              min={1}
              max={2000}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Math.min(2000, parseInt(e.target.value) || 1)))}
              disabled={loading}
            />
            <small className="form-helper">{t('barcode.quantityHint')}</small>
          </div>
        </div>

        <div className="barcode-print-actions">
          <button className="ims-btn ims-btn-secondary" onClick={onClose} disabled={loading} type="button">
            {t('common.close')}
          </button>
          <button className="ims-btn ims-btn-primary" onClick={handlePreview} disabled={loading} type="button">
            {loading ? <span className="ims-btn-spinner" /> : <Printer size={14} strokeWidth={2} />}
            {t('barcode.previewPdf')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarcodePrintModal;
